import { AppRoutingModule } from './app-routing.module';
import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { AppComponent } from './app.component';
import { CardComponent } from './components/shared/card/card.component';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatRadioModule } from '@angular/material/radio';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule, MAT_DATE_LOCALE } from '@angular/material/core';
import { NouisliderModule } from 'ng2-nouislider';
import { AngularResizedEventModule } from 'angular-resize-event';
import { DropdownComponent } from './components/shared/dropdown/dropdown.component';
import { InputComponent } from './components/shared/input/input.component';
import { TextareaComponent } from './components/shared/textarea/textarea.component';
import { RadioComponent } from './components/shared/radio/radio.component';
import { DeeplinkButtonComponent } from './components/shared/deeplink-button/deeplink-button.component';
import { SliderComponent } from './components/shared/slider/slider.component';
import { CheckboxComponent } from './components/shared/checkbox/checkbox.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { JsonLoader } from '../language/loader';
import { BasicInfoService } from './services/basic-info.service';
import { FileUploaderComponent } from './components/shared/file-uploader/file-uploader.component';
import { HttpClientModule } from '@angular/common/http';
import { FileInputAccessorModule } from 'file-input-accessor';
import { LeadApiService } from './services/api/lead-api.service';
import { CompanyApiService } from './services/api/company-api.service';
import { LoginApiService } from './services/api/login-api.service';
import { AuthService } from './services/auth.service';
import { RoutedComponents } from './app-routing.module';
import { NavbarComponent } from './components/navbar/navbar.component';
import { SearchComponent } from './components/shared/search/search.component';
import { FooterComponent } from './components/footer/footer.component';
import { ContactsApiService } from './services/api/contacts-api.service';
import { ApplicationsApiService } from './services/api/applications-api.service';
import { InfoboxComponent } from './components/shared/infobox/infobox.component';
import { PersonalInfoService } from './services/personal-info.service';
import { ApplicationService } from './services/application-service';
import { LocalizedDatePipe } from './pipes/localized-date.pipe';
import { CurrencyInputComponent } from './components/shared/currency-input/currency-input.component';
import { LocaleService } from './services/locale.service';
import { ContactsService } from './services/contacts.service';
import { TrackingService } from './services/tracking.service';
import { DialogComponent } from './components/shared/dialog/dialog.component';
import { MatDialogModule } from '@angular/material/dialog';
import { PurposeComponent } from './components/screens/purpose/purpose.component';
import { MatExpansionModule } from '@angular/material/expansion';
import { ExpansionContainerComponent } from './components/shared/expansion-container/expansion-container.component';
import { PersonalInfoComponent } from './components/personal-info/personal-info.component';
import { ContactComponent } from './components/shared/contact/contact.component';
import { environment } from '../environments/local/environment';
import { CookieModule } from 'ngx-cookie';
import { MyCurrencyPipe } from './pipes/currency-pipe.pipe';
import { FlowService } from './services/flow.service';
import { SharedModule } from '../../../shared/src/app/app.module';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { KlarnaApi } from './services/api/klarna-api.service';
import { FileListComponent } from './components/shared/file-list/file-list.component';
import { FileApiService } from 'src/app/services/api/file-api.service';
import { ResellerApiService } from 'src/app/services/api/reseller-api.service';
import { IdinApiService } from 'src/app/services/api/idin-api.service';
import { StorageService } from 'src/app/services/storage.service';
import { IdentificationGuard } from 'src/app/services/identification.guard';
import { DatepickerComponent } from './components/shared/datepicker/datepicker.component';
import { CreddoSharedLibraryModule } from 'creddo-shared-library';
import { ApplicationStepsComponent } from './components/application-steps/application-steps.component';
import { SliceGenericPrefixPipe } from './pipes/slice-generic-prefix.pipe';
import { UnsubscribeApiService } from './services/api/unsubscribe-api.service';
import { NgxHotjarModule, NgxHotjarRouterModule } from 'ngx-hotjar';

declare global {
  interface Window {
    onXS2AReady: any;
    XS2A: {
      configure: any;
      startFlow: any;
    };
  }
  const HubSpotConversations: {
    widget: {
      remove: () => void;
      open: () => void;
    };
  };
}

@NgModule({
  declarations: [
    AppComponent,
    CardComponent,
    CheckboxComponent,
    DeeplinkButtonComponent,
    DropdownComponent,
    InputComponent,
    DatepickerComponent,
    TextareaComponent,
    RadioComponent,
    CheckboxComponent,
    SliderComponent,
    FileUploaderComponent,
    SearchComponent,
    NavbarComponent,
    ApplicationStepsComponent,
    FooterComponent,
    InfoboxComponent,
    CurrencyInputComponent,
    DialogComponent,
    PurposeComponent,
    PersonalInfoComponent,
    ContactComponent,
    ExpansionContainerComponent,
    FileListComponent,
    RoutedComponents,
    // Pipes
    LocalizedDatePipe,
    MyCurrencyPipe,
    SliceGenericPrefixPipe,
  ],
  imports: [
    AppRoutingModule,
    BrowserAnimationsModule,
    BrowserModule,
    FormsModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatCardModule,
    MatCheckboxModule,
    MatDialogModule,
    MatIconModule,
    MatInputModule,
    MatRadioModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatRadioModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatNativeDateModule,
    NouisliderModule,
    HttpClientModule,
    FileInputAccessorModule,
    MatExpansionModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: JsonLoader,
      },
    }),
    CookieModule.forRoot(),
    AngularResizedEventModule,
    SharedModule,
    NgxSkeletonLoaderModule,
    CreddoSharedLibraryModule,
    NgxHotjarModule.forRoot(environment.hj),
    NgxHotjarRouterModule,
  ],
  providers: [
    BasicInfoService,
    PersonalInfoService,
    CompanyApiService,
    LoginApiService,
    ContactsApiService,
    ApplicationsApiService,
    AuthService,
    LeadApiService,
    ApplicationService,
    LocaleService,
    ContactsService,
    TrackingService,
    FlowService,
    KlarnaApi,
    FileApiService,
    ResellerApiService,
    IdinApiService,
    StorageService,
    UnsubscribeApiService,
    IdentificationGuard,
    MatDatepickerModule,
    MatNativeDateModule,
    { provide: 'googleTagManagerId', useValue: environment.gtmId },
    { provide: Window, useValue: window },
    { provide: MAT_DATE_LOCALE, useValue: environment.locale },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
